import { useState, useRef, useEffect } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { GoogleLogin } from "@react-oauth/google";
import { track } from "../utils/tracking";
import FAQ from "./FAQ";
import { useUserCurrency } from "../hooks/useUserCurrency";
import {
  Frown, TrendingDown, Truck, Target, FolderUp, Zap, TrendingUp,
  Calculator, AlertTriangle, Package, AlertCircle, BarChart3,
  Rocket, DollarSign, Search, Trophy,
} from "lucide-react";

// Mapa emoji → componente Lucide. Reemplaza emojis decorativos por íconos vectoriales
// manteniendo las traducciones intactas (siguen leyendo "icon: '📉'" como string).
const EMOJI_TO_ICON = {
  "😰": Frown,
  "📉": TrendingDown,
  "🚚": Truck,
  "🎯": Target,
  "📁": FolderUp,
  "⚡": Zap,
  "📈": TrendingUp,
  "🧮": Calculator,
  "🚨": AlertTriangle,
  "📦": Package,
  "😮": AlertCircle,
  "📊": BarChart3,
  "🚀": Rocket,
  "💰": DollarSign,
  "🔍": Search,
  "🏆": Trophy,
};

function EmojiIcon({ emoji, size = 24, color, strokeWidth = 1.75 }) {
  const Icon = EMOJI_TO_ICON[emoji];
  if (!Icon) return <span style={{ fontSize: size }}>{emoji}</span>;
  return <Icon size={size} color={color} strokeWidth={strokeWidth} />;
}

const API_URL     = process.env.REACT_APP_API_URL  || "http://localhost:8000";
const PAYMENT_URL = process.env.REACT_APP_PAYMENT_URL || null;
const PAYMENT_URL_ANUAL = process.env.REACT_APP_PAYMENT_URL_ANUAL || null;
// Plan anual = $143.88 USD (20% off vs $14.99 × 12 = $179.88).
// Mostramos el equivalente mensual ($11.99) como gancho del ahorro.
const PRO_MONTHLY_PRICE = 14.99;
const PRO_ANNUAL_PRICE = 143.88;
const PRO_ANNUAL_MONTHLY_EQUIV = 11.99;
const PRO_DISCOUNT_PCT = 20;

const BILLING_LABELS = {
  es: { mensual: "Mensual", anual: "Anual", save: `Ahorra ${PRO_DISCOUNT_PCT}%`, perMonth: "USD / mes", billedAnnually: `facturado anual ($${PRO_ANNUAL_PRICE})` },
  pt: { mensual: "Mensal",  anual: "Anual", save: `Economize ${PRO_DISCOUNT_PCT}%`, perMonth: "USD / mês", billedAnnually: `cobrado anual ($${PRO_ANNUAL_PRICE})` },
  en: { mensual: "Monthly", anual: "Annual", save: `Save ${PRO_DISCOUNT_PCT}%`, perMonth: "USD / month", billedAnnually: `billed annually ($${PRO_ANNUAL_PRICE})` },
};

const C = {
  green:      "#059669", greenLight: "#10B981", greenDark: "#047857",
  greenBg:    "#ECFDF5", amber: "#F59E0B", amberBg: "#FFFBEB",
  orange:     "#F97316", dark: "#111827", medium: "#374151",
  light:      "#6B7280", border: "#E5E7EB", bgLight: "#F9FAFB", white: "#FFFFFF",
};

// ── Traducciones ──────────────────────────────────────────────────────────────
const LANG_META = {
  es: { flag: "🇪🇸", label: "ES" },
  pt: { flag: "🇧🇷", label: "PT" },
  en: { flag: "🇺🇸", label: "EN" },
};

const T = {
  es: {
    nav_how: "Cómo funciona", nav_plans: "Planes", nav_login: "Iniciar sesión", nav_cta: "Empezar gratis",
    badge: "💰 Rentabilidad real para dropshippers en LATAM",
    h1a: "¿Tu dropshipping", h1b: "realmente te deja dinero?",
    sub: "En pago contra entrega, el 'costo por resultado' de Meta está calculado para otro modelo de negocio. Zenviq cruza los datos reales de Dropi con Meta y te muestra cuánto te costó cada cliente que efectivamente pagó.",
    cta1: "🚀 Empezar gratis — sin tarjeta", cta2: "Ver cómo funciona →",
    social: "Dropshippers en LATAM calculan su margen real con Zenviq",
    trust1: "Construido para Dropi", trust2: "Gratis para empezar", trust3: "Colombia, México y Chile",
    latam_title: "Para dropshippers de LATAM",
    latam_sub: "Enfocado en Colombia, México y Chile — donde el dropshipping con Dropi tiene más volumen",
    problem_label: "¿Te suena familiar?",
    problem_h2a: "La mayoría de los dropshippers",
    problem_h2b: "no sabe si está ganando o perdiendo",
    problem_sub: "Vendes, recibes pagos, pero al final del mes los números no cuadran. Aquí está el problema.",
    problems: [
      { icon:"😰", title:"Vendes mucho pero no ganas", text:"Tu volumen de ventas es alto pero no sabes si tus márgenes son positivos." },
      { icon:"📉", title:"El número de Meta que usas está calculado para otro modelo", text:"En pago contra entrega, solo ~55 de cada 100 leads pagan. Meta calcula el CPR sobre los 100. Tu costo real por cliente pagado es casi el doble de lo que ves en Meta Ads Manager." },
      { icon:"🚚", title:"No sabes qué transportadora te hunde", text:"Cada operador tiene distinta tasa de entrega y afecta tu costo real." },
      { icon:"🎯", title:"No identificas qué producto renta", text:"Alto volumen no es igual a alta rentabilidad. ¿Cuál te da ganancia real?" },
    ],
    solution_label: "La solución", solution_h2: "Zenviq hace el trabajo por ti",
    solution_sub: "Sube tus archivos de Dropi y Meta Ads, y en segundos tienes un panel completo con tu rentabilidad real.",
    steps: [
      { n:"1", icon:"📁", title:"Sube tus archivos", text:"Exporta tu reporte de Dropi y tu resumen de Meta Ads. Zenviq los lee automáticamente." },
      { n:"2", icon:"⚡", title:"Zenviq los cruza", text:"Nuestro motor conecta cada campaña con sus ventas y calcula el costo real de cada pedido entregado." },
      { n:"3", icon:"📈", title:"Ves tu rentabilidad real", text:"Margen neto real, costo de adquisición por pagador, ranking de productos y transportadoras — todo en un solo panel." },
    ],
    features: [
      { icon:"🧮", title:"Calculadora de precio real — no de fantasía", text:"La única que incluye fletes fallidos, tasa de confirmación, tasa de entrega y el costo real de adquisición sobre pedidos pagados. No el precio que quieres cobrar — el precio que necesitas cobrar para no perder dinero." },
      { icon:"📈", title:"Dashboard visual con 4 gráficos clave", text:"Pedidos por día, productos más vendidos, margen real y fletes fallidos — todo filtrado por el período que elijas. Ver para creer." },
      { icon:"🚨", title:"Alertas tempranas con desglose explicado", text:"Detecta automáticamente ROAS bajo, márgenes negativos y baja tasa de entrega. Te muestra cómo se calculó cada alerta para que entiendas qué corregir." },
      { icon:"📦", title:"Rentabilidad por producto y transportadora", text:"Descubre qué SKU te da ganancia real y qué operador logístico te conviene más — con datos reales, no estimaciones." },
    ],
    stats_h2: "Lo que descubren los dropshippers al usar Zenviq por primera vez",
    stats: [
      { stat:"3 de 5", desc:"dropshippers descubren que tienen margen negativo en al menos 1 producto al calcular los costos reales", icon:"😮" },
      { stat:"-40%", desc:"es la diferencia promedio entre el margen estimado y el margen real al incluir todos los costos del modelo COD", icon:"📉" },
      { stat:"55 de 100", desc:"leads de Meta terminan en un pago real en pago contra entrega. La mayoría calcula su precio usando el costo por resultado sobre los 100.", icon:"📊" },
    ],
    testi_label: "Lo que dicen nuestros usuarios",
    testimonials: [
      { text:"Descubrí que mi producto más vendido me daba pérdidas. Zenviq me lo mostró en 5 minutos.", name:"María G.", country:"🇨🇴 Colombia" },
      { text:"Descubrí que el número de Meta que usaba para calcular mi precio estaba mal. Mi costo real por pedido pagado era $1.50 USD más alto. Ajusté el precio y el margen se recuperó.", name:"Carlos M.", country:"🇲🇽 México" },
      { text:"Finalmente entiendo qué transportadora me conviene más. Ahorré $200 USD el primer mes.", name:"Ana P.", country:"🇨🇱 Chile" },
    ],
    plans_h2: "Planes simples, sin sorpresas", plans_sub: "Empieza gratis. Pasa a Pro cuando necesites más.",
    free_label:"Free", free_price:"$0", free_sub:"Para siempre · Sin tarjeta",
    free_features:[
      "Calculadora de precio real — incluye fletes fallidos, tasa de entrega, tasa de confirmación y Meta Ads. Los costos que nadie quiere que cuentes.",
      "Tabla de pedidos — buscá por número, producto o guía",
      "Wallet Dropi — entradas, salidas y retiros de tu billetera",
      "Carga de archivos Dropi",
    ],
    free_cta:"Empezar gratis",
    pro_label:"Pro", pro_badge:"⭐ Recomendado", pro_price:"$14.99", pro_price_sub:"USD / mes", pro_sub:"Análisis completo de rentabilidad",
    pro_features:[
      "Todo lo incluido en Free",
      "Dashboard visual — 4 gráficos: pedidos/día, productos más vendidos, margen real y fletes fallidos por período",
      "Alertas tempranas — detecta ROAS bajo, márgenes negativos y baja entrega, con desglose explicado de cómo se calculó cada alerta",
      "Panel Pulso — pedidos, entregas, ingresos y ROAS por período con comparativa vs período anterior",
      "Rentabilidad real por producto — margen neto descontando Meta Ads y fletes",
      "ROAS real por campaña — qué publicidad te deja dinero de verdad",
      "Ranking de transportadoras — quién te entrega más a menor costo real",
      "Cruce Meta Ads + Dropi — cada peso gastado en publicidad contra ventas reales",
    ],
    pro_cta:"Contratar Pro →",
    login_icon:"🚀", login_h2:"Empieza a conocer tu rentabilidad real hoy",
    login_sub:"Gratis, sin tarjeta, sin contratos. Accede en segundos con tu cuenta de Google.",
    login_verifying:"Verificando...", login_terms:"Sin tarjeta · Sin contratos · Cancela cuando quieras",
    footer_rights:"Todos los derechos reservados",
    chart_label:"Rentabilidad mensual", chart_months:["E","F","M","A","M","J","J"],
    chart_kpis:[{l:"ROAS real",v:"2.4x"},{l:"Margen neto",v:"18.4%"},{l:"Mejor prod.",v:"Prod. A"}],
  },
  pt: {
    nav_how: "Como funciona", nav_plans: "Planos", nav_login: "Entrar", nav_cta: "Começar grátis",
    badge: "💰 Rentabilidade real para dropshippers no LATAM",
    h1a: "Seu dropshipping", h1b: "realmente te dá dinheiro?",
    sub: "No pagamento na entrega, o 'custo por resultado' do Meta é calculado para outro modelo de negócio. O Zenviq cruza os dados reais do Dropi com o Meta e mostra quanto custou cada cliente que efetivamente pagou.",
    cta1: "🚀 Começar grátis — sem cartão", cta2: "Ver como funciona →",
    social: "Dropshippers no LATAM já calculam sua margem real com Zenviq",
    trust1: "Criado para o Dropi", trust2: "Grátis para começar", trust3: "Colômbia, México e Chile",
    latam_title: "Para dropshippers do LATAM",
    latam_sub: "Focado na Colômbia, México e Chile — onde o dropshipping com Dropi tem mais volume",
    problem_label: "Isso te parece familiar?",
    problem_h2a: "A maioria dos dropshippers",
    problem_h2b: "não sabe se está ganhando ou perdendo",
    problem_sub: "Você vende, recebe pagamentos, mas no final do mês os números não fecham. Aqui está o problema.",
    problems: [
      { icon:"😰", title:"Vende muito mas não lucra", text:"Seu volume de vendas é alto, mas você não sabe se suas margens são positivas." },
      { icon:"📉", title:"O número do Meta que você usa foi calculado para outro modelo", text:"No pagamento na entrega, apenas ~55 de cada 100 leads pagam. O Meta calcula o CPR sobre os 100. Seu custo real por cliente pagante é quase o dobro do que você vê no Meta Ads Manager." },
      { icon:"🚚", title:"Não sabe qual transportadora te prejudica", text:"Cada operador tem taxa de entrega diferente e afeta seu custo real." },
      { icon:"🎯", title:"Não identifica qual produto rende", text:"Alto volume não é igual a alta rentabilidade. Qual realmente te dá lucro?" },
    ],
    solution_label: "A solução", solution_h2: "O Zenviq faz o trabalho por você",
    solution_sub: "Suba seus arquivos do Dropi e Meta Ads, e em segundos você tem um painel completo com sua rentabilidade real.",
    steps: [
      { n:"1", icon:"📁", title:"Suba seus arquivos", text:"Exporte seu relatório do Dropi e seu resumo do Meta Ads. O Zenviq os lê automaticamente." },
      { n:"2", icon:"⚡", title:"O Zenviq os cruza", text:"Nosso motor conecta cada campanha com suas vendas e calcula o custo real de cada pedido entregue." },
      { n:"3", icon:"📈", title:"Veja sua rentabilidade real", text:"Margem líquida real, custo de aquisição por pagante, ranking de produtos e transportadoras — tudo em um só painel." },
    ],
    features: [
      { icon:"🧮", title:"Calculadora de preço real — não de fantasia", text:"A única que inclui fretes com falha, taxa de confirmação, taxa de entrega e o custo real de aquisição sobre pedidos pagos. Não o preço que você quer cobrar — o preço que você precisa cobrar para não perder dinheiro." },
      { icon:"📈", title:"Dashboard visual com 4 gráficos principais", text:"Pedidos por dia, produtos mais vendidos, margem real e fretes com falha — tudo filtrado pelo período que você escolher." },
      { icon:"🚨", title:"Alertas antecipados com explicação detalhada", text:"Detecta automaticamente ROAS baixo, margens negativas e baixa taxa de entrega. Mostra como cada alerta foi calculado para você saber o que corrigir." },
      { icon:"📦", title:"Rentabilidade por produto e transportadora", text:"Descubra qual SKU te dá lucro real e qual operador logístico te convém mais — com dados reais, não estimativas." },
    ],
    stats_h2: "O que os dropshippers descobrem ao usar o Zenviq pela primeira vez",
    stats: [
      { stat:"3 de 5", desc:"dropshippers descobrem que têm margem negativa em pelo menos 1 produto ao calcular os custos reais", icon:"😮" },
      { stat:"-40%", desc:"é a diferença média entre a margem estimada e a margem real ao incluir todos os custos do modelo COD", icon:"📉" },
      { stat:"55 de 100", desc:"leads do Meta terminam em um pagamento real no dropshipping com pagamento na entrega. A maioria calcula o preço usando o custo por resultado sobre os 100.", icon:"📊" },
    ],
    testi_label: "O que dizem nossos usuários",
    testimonials: [
      { text:"Descobri que meu produto mais vendido me dava prejuízo. O Zenviq me mostrou isso em 5 minutos.", name:"Maria G.", country:"🇨🇴 Colômbia" },
      { text:"Descobri que o número do Meta que eu usava para calcular meu preço estava errado. Meu custo real por pedido pago era US$1,50 mais alto. Ajustei o preço e a margem se recuperou.", name:"Carlos M.", country:"🇲🇽 México" },
      { text:"Finalmente entendo qual transportadora me convém mais. Economizei US$200 no primeiro mês.", name:"Ana P.", country:"🇨🇱 Chile" },
    ],
    plans_h2: "Planos simples, sem surpresas", plans_sub: "Comece grátis. Passe para o Pro quando precisar de mais.",
    free_label:"Free", free_price:"$0", free_sub:"Para sempre · Sem cartão",
    free_features:[
      "Calculadora de preço real — inclui fretes com falha, taxa de entrega, taxa de confirmação e Meta Ads. Os custos que ninguém quer que você conte.",
      "Tabela de pedidos — busque por número, produto ou guia",
      "Wallet Dropi — entradas, saídas e retiradas da sua carteira",
      "Upload de arquivos Dropi",
    ],
    free_cta:"Começar grátis",
    pro_label:"Pro", pro_badge:"⭐ Recomendado", pro_price:"$14.99", pro_price_sub:"USD / mês", pro_sub:"Análise completa de rentabilidade",
    pro_features:[
      "Tudo incluído no Free",
      "Dashboard visual — 4 gráficos: pedidos/dia, produtos mais vendidos, margem real e fretes com falha por período",
      "Alertas antecipados — detecta ROAS baixo, margens negativas e baixa entrega, com explicação detalhada de como cada alerta foi calculado",
      "Painel Pulso — pedidos, entregas, receitas e ROAS por período com comparativo vs período anterior",
      "Rentabilidade real por produto — margem líquida descontando Meta Ads e fretes",
      "ROAS real por campanha — qual publicidade realmente te dá dinheiro",
      "Ranking de transportadoras — quem entrega mais com menor custo real",
      "Cruzamento Meta Ads + Dropi — cada real gasto em publicidade vs vendas reais",
    ],
    pro_cta:"Contratar Pro →",
    login_icon:"🚀", login_h2:"Comece a conhecer sua rentabilidade real hoje",
    login_sub:"Grátis, sem cartão, sem contratos. Acesse em segundos com sua conta Google.",
    login_verifying:"Verificando...", login_terms:"Sem cartão · Sem contratos · Cancele quando quiser",
    footer_rights:"Todos os direitos reservados",
    chart_label:"Rentabilidade mensal", chart_months:["J","F","M","A","M","J","J"],
    chart_kpis:[{l:"ROAS real",v:"2.4x"},{l:"Margem líquida",v:"18.4%"},{l:"Melhor prod.",v:"Prod. A"}],
  },
  en: {
    nav_how: "How it works", nav_plans: "Plans", nav_login: "Log in", nav_cta: "Start free",
    badge: "💰 Real profitability for LATAM dropshippers",
    h1a: "Does your dropshipping", h1b: "really make you money?",
    sub: "In cash-on-delivery dropshipping, Meta's 'cost per result' is calculated for a different business model. Zenviq connects real Dropi data with Meta and shows you how much each paying customer actually cost.",
    cta1: "🚀 Start free — no credit card", cta2: "See how it works →",
    social: "LATAM dropshippers calculating their real margin with Zenviq",
    trust1: "Built for Dropi", trust2: "Free to start", trust3: "Colombia, Mexico & Chile",
    latam_title: "For LATAM dropshippers",
    latam_sub: "Focused on Colombia, Mexico and Chile — where Dropi dropshipping is most active",
    problem_label: "Sound familiar?",
    problem_h2a: "Most dropshippers",
    problem_h2b: "don't know if they're making or losing money",
    problem_sub: "You sell, you get paid, but at the end of the month the numbers don't add up. Here's why.",
    problems: [
      { icon:"😰", title:"You sell a lot but don't profit", text:"Your sales volume is high but you don't know if your margins are positive." },
      { icon:"📉", title:"The Meta number you use is calculated for a different model", text:"In cash-on-delivery, only ~55 of every 100 leads actually pay. Meta calculates CPR over all 100. Your real cost per paying customer is nearly double what Meta Ads Manager shows." },
      { icon:"🚚", title:"You don't know which carrier hurts you", text:"Each carrier has a different delivery rate that directly affects your real cost." },
      { icon:"🎯", title:"You can't tell which product is profitable", text:"High volume doesn't mean high profitability. Which one actually makes you money?" },
    ],
    solution_label: "The solution", solution_h2: "Zenviq does the work for you",
    solution_sub: "Upload your Dropi and Meta Ads files and get a complete profitability panel in seconds.",
    steps: [
      { n:"1", icon:"📁", title:"Upload your files", text:"Export your Dropi report and Meta Ads summary. Zenviq reads them automatically." },
      { n:"2", icon:"⚡", title:"Zenviq connects them", text:"Our engine links each campaign to its sales and calculates the real cost of each delivered order." },
      { n:"3", icon:"📈", title:"See your real profitability", text:"Real net margin, acquisition cost per paying customer, product ranking and carrier ranking — all in one panel." },
    ],
    features: [
      { icon:"🧮", title:"Real price calculator — not fantasy math", text:"The only calculator that includes failed shipping, confirmation rate, delivery rate and real acquisition cost per paying customer. Not the price you want to charge — the price you need to charge to actually make money." },
      { icon:"📈", title:"Visual dashboard with 4 key charts", text:"Daily orders, top products, real margin and failed shipments — all filtered by the period you choose." },
      { icon:"🚨", title:"Early alerts with detailed explanations", text:"Automatically detects low ROAS, negative margins and poor delivery rates. Shows exactly how each alert was calculated so you know what to fix." },
      { icon:"📦", title:"Profitability by product and carrier", text:"Find out which SKU actually makes you money and which logistics operator gives you the best results — with real data, not estimates." },
    ],
    stats_h2: "What dropshippers discover when they first use Zenviq",
    stats: [
      { stat:"3 in 5", desc:"dropshippers find they have negative margin in at least 1 product when calculating real costs", icon:"😮" },
      { stat:"-40%", desc:"average gap between estimated margin and real margin when all COD model costs are included", icon:"📉" },
      { stat:"55 in 100", desc:"Meta leads result in an actual payment in cash-on-delivery dropshipping. Most calculate their price using cost per result over all 100.", icon:"📊" },
    ],
    testi_label: "What our users say",
    testimonials: [
      { text:"I discovered my best-selling product was losing me money. Zenviq showed me in 5 minutes.", name:"Maria G.", country:"🇨🇴 Colombia" },
      { text:"I found out the Meta number I used to calculate my price was wrong. My real cost per paid order was $1.50 USD higher. I adjusted the price and the margin came back.", name:"Carlos M.", country:"🇲🇽 Mexico" },
      { text:"I finally understand which carrier works best for me. I saved $200 in my first month.", name:"Ana P.", country:"🇨🇱 Chile" },
    ],
    plans_h2: "Simple plans, no surprises", plans_sub: "Start free. Upgrade to Pro when you need more.",
    free_label:"Free", free_price:"$0", free_sub:"Forever · No credit card",
    free_features:[
      "Real price calculator — includes failed shipping costs, delivery rate, confirmation rate & Meta Ads. The costs nobody wants you to count.",
      "Orders table — search by number, product or tracking",
      "Dropi Wallet — income, expenses and withdrawals",
      "Dropi file uploads",
    ],
    free_cta:"Start free",
    pro_label:"Pro", pro_badge:"⭐ Recommended", pro_price:"$14.99", pro_price_sub:"USD / month", pro_sub:"Full profitability analysis",
    pro_features:[
      "Everything in Free",
      "Visual Dashboard — 4 charts: daily orders, top products, real margin and failed shipments by period",
      "Early alerts — detects low ROAS, negative margins and low delivery rates, with a detailed breakdown of how each alert was calculated",
      "Pulse panel — orders, deliveries, revenue and ROAS by period with previous period comparison",
      "Real profitability by product — net margin after Meta Ads and shipping costs",
      "Real ROAS by campaign — which ads actually make you money",
      "Carrier ranking — who delivers more at the lowest real cost",
      "Meta Ads + Dropi crossover — every ad dollar against real sales",
    ],
    pro_cta:"Get Pro →",
    login_icon:"🚀", login_h2:"Start knowing your real profitability today",
    login_sub:"Free, no credit card, no contracts. Sign in with Google in seconds.",
    login_verifying:"Verifying...", login_terms:"No credit card · No contracts · Cancel anytime",
    footer_rights:"All rights reserved",
    chart_label:"Monthly profitability", chart_months:["J","F","M","A","M","J","J"],
    chart_kpis:[{l:"Real ROAS",v:"2.4x"},{l:"Net margin",v:"18.4%"},{l:"Best prod.",v:"Prod. A"}],
  },
};

const LATAM_COUNTRIES = [
  { flag:"🇨🇴", name:{ es:"Colombia", pt:"Colômbia", en:"Colombia" } },
  { flag:"🇲🇽", name:{ es:"México",   pt:"México",   en:"Mexico"   } },
  { flag:"🇨🇱", name:{ es:"Chile",    pt:"Chile",    en:"Chile"    } },
];

// ── Logo ──────────────────────────────────────────────────────────────────────
function ZenviqLogo({ size = 36, showName = true, dark = true }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
        <rect width="40" height="40" rx="10" fill={C.green}/>
        <polyline points="10,11 30,11 10,29 30,29" stroke="white" strokeWidth="3.8"
          strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <polyline points="9,36 15,32 22,34 31,28" stroke="rgba(255,255,255,0.45)"
          strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      </svg>
      {showName && (
        <span style={{
          fontSize:size*0.58, fontWeight:800,
          color:dark ? C.dark : C.white,
          letterSpacing:"-0.04em", fontFamily:"'Space Grotesk', sans-serif",
        }}>Zenviq</span>
      )}
    </div>
  );
}

// ── Language Switcher ─────────────────────────────────────────────────────────
function LanguageSwitcher({ lang, setLang }) {
  return (
    <div style={{
      display:"flex", flexDirection:"row", gap:4,
      background:"rgba(0,0,0,0.04)", borderRadius:20, padding:"3px 6px",
    }}>
      {Object.entries(LANG_META).map(([code, meta]) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          title={code.toUpperCase()}
          style={{
            width:28, height:28, borderRadius:"50%",
            border: lang === code ? `2px solid ${C.green}` : "2px solid transparent",
            background: lang === code ? C.greenBg : "transparent",
            cursor:"pointer", fontSize:14,
            display:"flex", alignItems:"center", justifyContent:"center",
            transition:"all 0.2s ease",
          }}
        >
          {meta.flag}
        </button>
      ))}
    </div>
  );
}

// ── Gráfica de rentabilidad ───────────────────────────────────────────────────
function ProfitChart({ t }) {
  const [on, setOn] = useState(false);
  useEffect(() => { const id = setTimeout(() => setOn(true), 400); return () => clearTimeout(id); }, []);
  const bars = [40,55,48,70,62,85,95];

  return (
    <div style={{
      background:C.white, borderRadius:24, padding:"28px 32px",
      boxShadow:"0 20px 60px rgba(0,0,0,0.10)",
      border:`1px solid ${C.border}`, maxWidth:420, width:"100%",
    }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div>
          <div style={{ fontSize:12, color:C.light, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>
            {t.chart_label}
          </div>
          <div style={{ fontSize:28, fontWeight:900, color:C.dark, letterSpacing:"-0.04em", marginTop:2 }}>+24.3%</div>
        </div>
        <div style={{ background:C.greenBg, color:C.green, borderRadius:20, padding:"6px 14px", fontSize:13, fontWeight:700 }}>
          ↑ {t.chart_kpis[0].l}
        </div>
      </div>
      <div style={{ display:"flex", alignItems:"flex-end", gap:8, height:100, marginBottom:16 }}>
        {bars.map((h, i) => (
          <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", justifyContent:"flex-end" }}>
            <div style={{
              background: i < 3 ? C.greenLight : i < 5 ? C.green : C.greenDark,
              borderRadius:"4px 4px 0 0",
              height: on ? h : 0,
              transition:`height 0.6s cubic-bezier(0.34,1.56,0.64,1) ${i*80}ms`,
            }}/>
          </div>
        ))}
      </div>
      <div style={{ display:"flex", gap:8, marginBottom:20 }}>
        {t.chart_months.map((m,i) => (
          <div key={i} style={{ flex:1, textAlign:"center", fontSize:10, color:C.light, fontWeight:600 }}>{m}</div>
        ))}
      </div>
      <div style={{ display:"flex", gap:10 }}>
        {[
          { ...t.chart_kpis[0], icon:"📈", bg:C.greenBg, color:C.green },
          { ...t.chart_kpis[1], icon:"💰", bg:C.amberBg, color:C.amber },
          { ...t.chart_kpis[2], icon:"🏆", bg:"#FFF7ED", color:C.orange },
        ].map((k,i) => (
          <div key={i} style={{ flex:1, background:k.bg, borderRadius:12, padding:"10px 8px", textAlign:"center" }}>
            <div style={{ marginBottom:3, color:k.color, display:"flex", justifyContent:"center" }}>
              <EmojiIcon emoji={k.icon} size={18} />
            </div>
            <div style={{ fontSize:13, fontWeight:800, color:k.color }}>{k.v}</div>
            <div style={{ fontSize:9, color:C.light, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.03em" }}>{k.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Check ─────────────────────────────────────────────────────────────────────
function Check({ children }) {
  return (
    <div style={{ display:"flex", gap:10, marginBottom:10, alignItems:"flex-start" }}>
      <span style={{
        width:20, height:20, borderRadius:"50%", flexShrink:0, marginTop:1,
        background:C.greenBg, display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:10, fontWeight:800, color:C.green,
      }}>✓</span>
      <span style={{ fontSize:14, color:C.medium, lineHeight:1.6 }}>{children}</span>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
// ── Modal legal (Términos / Privacidad) ───────────────────────────────────────
function ModalLegal({ tipo, onClose }) {
  const esTerminos = tipo === "terminos";
  return (
    <div
      onClick={onClose}
      style={{ position:"fixed", inset:0, zIndex:999, background:"rgba(0,0,0,0.55)",
        display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background:"#fff", borderRadius:20, maxWidth:680, width:"100%",
          maxHeight:"85vh", overflowY:"auto", padding:"clamp(24px, 5vw, 40px)",
          boxShadow:"0 24px 80px rgba(0,0,0,0.25)" }}
      >
        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
          <h2 style={{ fontSize:20, fontWeight:800, color:C.dark, margin:0 }}>
            {esTerminos ? "📋 Términos de Uso" : "🔒 Política de Privacidad"}
          </h2>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:22,
            cursor:"pointer", color:C.light, lineHeight:1 }}>✕</button>
        </div>

        <div style={{ fontSize:13, color:C.medium, lineHeight:1.8 }}>
          {esTerminos ? (
            <>
              <p style={{ color:C.light, marginBottom:20 }}>Última actualización: abril 2025 · Aplicable a todos los usuarios de Zenviq (zenviq.app)</p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>1. Aceptación</h3>
              <p>Al acceder o utilizar Zenviq, aceptas los presentes Términos de Uso. Si no estás de acuerdo, debes abstenerte de usar el servicio.</p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>2. Descripción del servicio</h3>
              <p>Zenviq es una herramienta de análisis de rentabilidad para dropshippers. Procesa archivos que el usuario carga (reportes de Dropi y Meta Ads) y genera indicadores financieros con fines informativos. <strong>No es una herramienta de contabilidad certificada ni reemplaza asesoría financiera o tributaria profesional.</strong></p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>3. Planes y facturación</h3>
              <p>Zenviq ofrece un plan gratuito (Free) y un plan de pago (Pro, USD $14.99/mes). Los cobros se realizan a través de la pasarela de pago indicada al momento de la suscripción. Las suscripciones se renuevan automáticamente salvo cancelación previa. No se emiten reembolsos por períodos parciales.</p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>4. Responsabilidad del usuario</h3>
              <p>El usuario es responsable de:</p>
              <ul style={{ paddingLeft:20, marginTop:8 }}>
                <li>La veracidad y exactitud de los archivos que carga.</li>
                <li>El uso que haga de los análisis generados.</li>
                <li>Mantener la confidencialidad de sus credenciales de acceso.</li>
                <li>No utilizar el servicio para fines ilícitos, fraudulentos o que violen derechos de terceros.</li>
              </ul>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>5. Limitación de responsabilidad</h3>
              <p>Zenviq se entrega "tal cual" (as-is). No garantizamos exactitud contable, disponibilidad continua del servicio ni resultados financieros específicos. En ningún caso seremos responsables por pérdidas económicas derivadas del uso o la imposibilidad de uso del servicio.</p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>6. Propiedad intelectual</h3>
              <p>Todo el código, diseño, marca y contenidos de Zenviq son propiedad exclusiva de sus creadores. Queda prohibida su reproducción total o parcial sin autorización escrita.</p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>7. Modificaciones</h3>
              <p>Nos reservamos el derecho de modificar estos Términos en cualquier momento. Los cambios serán notificados dentro de la plataforma. El uso continuado implica la aceptación de los nuevos Términos.</p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>8. Ley aplicable</h3>
              <p>Estos Términos se rigen por la legislación de la República de Chile. Ante cualquier disputa, las partes se someten a la jurisdicción de los tribunales competentes de Santiago de Chile.</p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>9. Contacto</h3>
              <p>Para consultas sobre estos Términos: <strong>hola@zenviq.app</strong></p>
            </>
          ) : (
            <>
              <p style={{ color:C.light, marginBottom:20 }}>Última actualización: abril 2025 · Aplicable a todos los usuarios de Zenviq (zenviq.app)</p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>1. Responsable del tratamiento</h3>
              <p>Zenviq (zenviq.app) es responsable del tratamiento de los datos personales que recopila de sus usuarios.</p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>2. Datos que recopilamos</h3>
              <ul style={{ paddingLeft:20, marginTop:8 }}>
                <li><strong>Autenticación Google:</strong> nombre y dirección de correo electrónico, obtenidos mediante Google OAuth con tu consentimiento explícito.</li>
                <li><strong>Archivos cargados:</strong> reportes de Dropi (pedidos, finanzas) y resúmenes de Meta Ads que el usuario sube voluntariamente para generar el análisis.</li>
                <li><strong>Datos de uso:</strong> vistas visitadas y acciones generales dentro de la plataforma, con fines de mejora del servicio (sin identificación individual en analytics externos).</li>
              </ul>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>3. Finalidad del tratamiento</h3>
              <p>Los datos se usan exclusivamente para:</p>
              <ul style={{ paddingLeft:20, marginTop:8 }}>
                <li>Autenticar al usuario y mantener su sesión.</li>
                <li>Procesar los archivos cargados y generar los análisis de rentabilidad.</li>
                <li>Mejorar las funcionalidades del servicio.</li>
              </ul>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>4. Compartición de datos</h3>
              <p><strong>No vendemos ni cedemos tus datos a terceros.</strong> Los archivos cargados son de uso exclusivo del usuario y no son accedidos por terceros salvo requerimiento legal expreso.</p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>5. Almacenamiento y seguridad</h3>
              <p>Los archivos se almacenan en servidores seguros (Railway) con acceso restringido. Las contraseñas no son almacenadas — la autenticación es 100% gestionada por Google OAuth.</p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>6. Derechos del usuario</h3>
              <p>Tienes derecho a solicitar en cualquier momento:</p>
              <ul style={{ paddingLeft:20, marginTop:8 }}>
                <li>Acceso a los datos que tenemos sobre ti.</li>
                <li>Eliminación de tu cuenta y todos los archivos asociados.</li>
                <li>Corrección de datos incorrectos.</li>
              </ul>
              <p style={{ marginTop:8 }}>Para ejercer estos derechos, contáctanos en <strong>hola@zenviq.app</strong>.</p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>7. Cookies</h3>
              <p>Zenviq solo utiliza cookies técnicas estrictamente necesarias para mantener la sesión del usuario. No usamos cookies de seguimiento ni publicidad.</p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>8. Ley aplicable</h3>
              <p>Esta política se rige por la Ley N° 19.628 sobre Protección de la Vida Privada (Chile) y normativas equivalentes en los países LATAM donde operamos.</p>

              <h3 style={{ fontSize:14, fontWeight:700, color:C.dark, marginTop:24, marginBottom:8 }}>9. Contacto</h3>
              <p>Consultas sobre privacidad: <strong>hola@zenviq.app</strong></p>
            </>
          )}
        </div>

        <button onClick={onClose} style={{
          marginTop:32, width:"100%", padding:"12px", borderRadius:10, border:"none",
          background:C.green, color:"#fff", fontSize:14, fontWeight:700,
          cursor:"pointer", fontFamily:"inherit",
        }}>
          Cerrar
        </button>
      </div>
    </div>
  );
}

export default function LandingPage({ onLogin }) {
  const [lang, setLang] = useState("es");
  const [loginError, setLoginError]     = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [menuAbierto, setMenuAbierto]   = useState(false);
  const [modalLegal, setModalLegal]     = useState(null); // "terminos" | "privacidad" | null
  const [billing, setBilling]           = useState("anual"); // "mensual" | "anual" — anual default empuja al ahorro
  const loginRef = useRef(null);
  const plansRef = useRef(null);
  const howRef   = useRef(null);
  const isMobile = useIsMobile();
  const { currency: userCurrency, formatLocal: formatLocalPrice } = useUserCurrency();
  const t = T[lang];

  // Precio Pro según billing seleccionado (mensual o anual).
  // El "anual" muestra el equivalente mensual ($11.99) con tag de ahorro.
  const billingLabels = BILLING_LABELS[lang] || BILLING_LABELS.es;
  const proPriceUSD = billing === "anual" ? PRO_ANNUAL_MONTHLY_EQUIV : PRO_MONTHLY_PRICE;
  const localPriceLabel = userCurrency !== "USD" ? formatLocalPrice(proPriceUSD) : null;
  const proCheckoutUrl = billing === "anual" ? (PAYMENT_URL_ANUAL || PAYMENT_URL) : PAYMENT_URL;

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior:"smooth", block:"start" });

  // Pageview al montar la landing
  useEffect(() => { track("pageview"); }, []);

  const handleGoogleSuccess = async (cred) => {
    setLoginLoading(true);
    setLoginError(null);
    track("click_signup");
    try {
      const res  = await fetch(`${API_URL}/api/auth/google`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ token:cred.credential }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al iniciar sesión");
      localStorage.setItem("token", data.token);
      onLogin(data.usuario);
    } catch (e) {
      setLoginError(e.message);
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div style={{
      fontFamily:"'Space Grotesk', -apple-system, sans-serif",
      background:C.white, color:C.dark,
      minHeight:"100vh", overflowX:"hidden",
    }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
        @keyframes float { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-8px); } }
        .fade1 { animation:fadeUp 0.7s ease both; }
        .fade2 { animation:fadeUp 0.7s ease 0.1s both; }
        .fade3 { animation:fadeUp 0.7s ease 0.2s both; }
        .fade4 { animation:fadeUp 0.7s ease 0.35s both; }
        .float { animation:float 3.5s ease-in-out infinite; }
        .card-hover { transition:transform 0.2s ease,box-shadow 0.2s ease; }
        .card-hover:hover { transform:translateY(-4px); box-shadow:0 20px 50px rgba(0,0,0,0.12); }
      `}</style>

      {/* ── NAVBAR ─────────────────────────────────────────────────────────── */}
      <nav style={{
        position:"sticky", top:0, zIndex:100,
        background:"rgba(255,255,255,0.96)", backdropFilter:"blur(16px)",
        borderBottom:`1px solid ${C.border}`,
        padding:"0 clamp(1rem, 5%, 7%)",
      }}>
        {/* Fila principal */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", height:64 }}>
          <ZenviqLogo size={32} dark />

          {/* Desktop links */}
          {!isMobile && (
            <div style={{ display:"flex", alignItems:"center", gap:16 }}>
              <LanguageSwitcher lang={lang} setLang={setLang} />
              <button onClick={() => scrollTo(howRef)} style={{ background:"none", border:"none", color:C.light, fontSize:14, cursor:"pointer", fontFamily:"inherit", fontWeight:500 }}>
                {t.nav_how}
              </button>
              <button onClick={() => scrollTo(plansRef)} style={{ background:"none", border:"none", color:C.light, fontSize:14, cursor:"pointer", fontFamily:"inherit", fontWeight:500 }}>
                {t.nav_plans}
              </button>
              <button onClick={() => scrollTo(loginRef)} style={{
                background:"none", border:`1.5px solid ${C.border}`, color:C.medium,
                borderRadius:10, padding:"8px 20px", fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:"inherit",
              }}>{t.nav_login}</button>
              <button onClick={() => scrollTo(loginRef)} style={{
                background:C.green, color:C.white, border:"none",
                borderRadius:10, padding:"9px 22px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
                boxShadow:`0 4px 16px rgba(5,150,105,0.3)`,
              }}>{t.nav_cta}</button>
            </div>
          )}

          {/* Mobile: selector idioma + botón empezar + hamburger */}
          {isMobile && (
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <LanguageSwitcher lang={lang} setLang={setLang} />
              <button onClick={() => scrollTo(loginRef)} style={{
                background:C.green, color:C.white, border:"none",
                borderRadius:10, padding:"8px 16px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
              }}>{t.nav_cta}</button>
              <button
                onClick={() => setMenuAbierto(v => !v)}
                style={{ background:"none", border:`1.5px solid ${C.border}`, borderRadius:8, padding:"6px 10px", cursor:"pointer", fontSize:18, lineHeight:1 }}
              >
                {menuAbierto ? "✕" : "☰"}
              </button>
            </div>
          )}
        </div>

        {/* Drawer móvil */}
        {isMobile && menuAbierto && (
          <div style={{
            borderTop:`1px solid ${C.border}`, padding:"16px 0 20px",
            display:"flex", flexDirection:"column", gap:4,
            background:"rgba(255,255,255,0.98)",
          }}>
            {[
              { label: t.nav_how,    ref: howRef   },
              { label: t.nav_plans,  ref: plansRef  },
              { label: t.nav_login,  ref: loginRef  },
            ].map(({ label, ref }) => (
              <button key={label} onClick={() => { scrollTo(ref); setMenuAbierto(false); }} style={{
                background:"none", border:"none", color:C.medium, fontSize:15,
                fontWeight:600, cursor:"pointer", fontFamily:"inherit",
                textAlign:"left", padding:"12px 4px",
                borderBottom:`1px solid ${C.border}`,
              }}>{label}</button>
            ))}
          </div>
        )}
      </nav>

      {/* ════ A — HERO ════ */}
      <section style={{ padding:"clamp(48px, 8vw, 90px) clamp(1rem, 7%, 7%) clamp(40px, 6vw, 80px)", background:`linear-gradient(160deg, ${C.white} 60%, ${C.greenBg} 100%)` }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"flex", alignItems:"center", gap:"clamp(24px, 5vw, 60px)", flexWrap:"wrap" }}>
          <div style={{ flex:"1 1 clamp(280px, 100%, 420px)", minWidth:0 }}>
            {/* Trust bar */}
            <div className="fade1" style={{ display:"flex", gap:20, flexWrap:"wrap", marginBottom:24 }}>
              {[t.trust1, t.trust2, t.trust3].map((item, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ width:7, height:7, borderRadius:"50%", background:C.green, display:"inline-block" }}/>
                  <span style={{ fontSize:12, color:C.medium, fontWeight:600 }}>{item}</span>
                </div>
              ))}
            </div>

            <h1 className="fade2" style={{
              fontSize:"clamp(2.4rem, 5vw, 3.8rem)",
              fontWeight:900, lineHeight:1.1, letterSpacing:"-0.04em", color:C.dark, marginBottom:20,
            }}>
              {t.h1a}<br/>
              <span style={{ background:`linear-gradient(90deg, ${C.green}, ${C.greenLight})`, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
                {t.h1b}
              </span>
            </h1>

            <p className="fade3" style={{ fontSize:"clamp(1rem, 2vw, 1.15rem)", color:C.light, lineHeight:1.75, maxWidth:480, marginBottom:36 }}>
              {t.sub}
            </p>

            <div className="fade4" style={{ display:"flex", gap:14, flexWrap:"wrap", marginBottom:36 }}>
              <button onClick={() => scrollTo(loginRef)} style={{
                background:C.green, color:C.white, border:"none", borderRadius:12, padding:"16px 32px",
                fontSize:16, fontWeight:800, cursor:"pointer", fontFamily:"inherit",
                boxShadow:`0 6px 24px rgba(5,150,105,0.35)`,
              }}>{t.cta1}</button>
              <button onClick={() => scrollTo(howRef)} style={{
                background:C.white, color:C.medium, border:`2px solid ${C.border}`,
                borderRadius:12, padding:"16px 28px", fontSize:16, fontWeight:600, cursor:"pointer", fontFamily:"inherit",
              }}>{t.cta2}</button>
            </div>

            {/* Social proof */}
            <div className="fade4" style={{ display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ display:"flex" }}>
                {["🧑‍💼","👩‍💼","🧑‍💻","👨‍💼"].map((e,i) => (
                  <div key={i} style={{
                    width:32, height:32, borderRadius:"50%",
                    background:[C.greenBg,C.amberBg,"#FFF7ED","#EEF2FF"][i],
                    border:"2px solid white", marginLeft:i>0 ? -8 : 0,
                    display:"flex", alignItems:"center", justifyContent:"center", fontSize:14,
                  }}>{e}</div>
                ))}
              </div>
              <span style={{ fontSize:13, color:C.light }}>{t.social}</span>
            </div>
          </div>

          {/* Gráfica — oculta en móvil */}
          {!isMobile && (
            <div className="float" style={{ flex:"1 1 360px", display:"flex", justifyContent:"center" }}>
              <ProfitChart t={t} />
            </div>
          )}
        </div>
      </section>

      {/* ════ LATAM PRESENCE STRIP ════ */}
      <section style={{ padding:"clamp(28px, 5vw, 40px) clamp(1rem, 7%, 7%)", background:C.dark, overflow:"hidden" }}>
        <div style={{ maxWidth:1000, margin:"0 auto", textAlign:"center" }}>
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:6 }}>
            {t.latam_title}
          </div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.25)", marginBottom:28 }}>{t.latam_sub}</div>
          <div style={{ display:"flex", justifyContent:"center", flexWrap:"wrap", gap:20 }}>
            {LATAM_COUNTRIES.map((c, i) => (
              <div key={i} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                <span style={{ fontSize:28 }}>{c.flag}</span>
                <span style={{ fontSize:10, color:"rgba(255,255,255,0.35)", fontWeight:600 }}>{c.name[lang]}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════ I — PROBLEMA ════ */}
      <section style={{ padding:"clamp(40px, 8vw, 80px) clamp(1rem, 7%, 7%)", background:C.bgLight }}>
        <div style={{ maxWidth:900, margin:"0 auto", textAlign:"center" }}>
          <div style={{ display:"inline-block", background:"#FEF3C7", color:C.amber, borderRadius:20, padding:"5px 16px", fontSize:12, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:20 }}>
            {t.problem_label}
          </div>
          <h2 style={{ fontSize:"clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight:900, letterSpacing:"-0.03em", color:C.dark, marginBottom:16 }}>
            {t.problem_h2a}<br/>{t.problem_h2b}
          </h2>
          <p style={{ fontSize:16, color:C.light, marginBottom:52, lineHeight:1.7 }}>{t.problem_sub}</p>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(clamp(160px, 80vw, 240px), 1fr))", gap:"clamp(12px, 3vw, 20px)" }}>
            {t.problems.map((p,i) => (
              <div key={i} className="card-hover" style={{
                background:C.white, borderRadius:18, padding:"28px 24px", textAlign:"left",
                border:`1px solid ${C.border}`, boxShadow:"0 2px 12px rgba(0,0,0,0.05)",
              }}>
                <div style={{ marginBottom:14, color:C.green }}>
                  <EmojiIcon emoji={p.icon} size={28} />
                </div>
                <div style={{ fontSize:15, fontWeight:800, color:C.dark, marginBottom:8 }}>{p.title}</div>
                <div style={{ fontSize:13, color:C.light, lineHeight:1.65 }}>{p.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════ D — SOLUCIÓN ════ */}
      <section ref={howRef} style={{ padding:"clamp(40px, 8vw, 90px) clamp(1rem, 7%, 7%)", background:C.white }}>
        <div style={{ maxWidth:1000, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:60 }}>
            <div style={{ display:"inline-block", background:C.greenBg, color:C.green, borderRadius:20, padding:"5px 16px", fontSize:12, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:20 }}>
              {t.solution_label}
            </div>
            <h2 style={{ fontSize:"clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight:900, letterSpacing:"-0.03em", color:C.dark, marginBottom:16 }}>
              {t.solution_h2}
            </h2>
            <p style={{ fontSize:16, color:C.light, maxWidth:520, margin:"0 auto", lineHeight:1.7 }}>{t.solution_sub}</p>
          </div>

          {/* Pasos */}
          <div style={{ display:"flex", gap:"clamp(12px, 3vw, 16px)", marginBottom:"clamp(36px, 6vw, 70px)", flexWrap:"wrap" }}>
            {t.steps.map((s,i) => {
              const colors = [
                { bg:C.greenBg, accent:C.green },
                { bg:C.amberBg, accent:C.amber },
                { bg:"#FFF7ED", accent:C.orange },
              ][i];
              return (
                <div key={i} style={{ flex:"1 1 clamp(240px, 100%, 260px)", background:colors.bg, borderRadius:20, padding:"clamp(24px, 4vw, 36px) clamp(20px, 3vw, 28px)", border:`2px solid ${colors.accent}20` }}>
                  <div style={{ width:40, height:40, borderRadius:"50%", background:colors.accent, color:C.white, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:900, marginBottom:16 }}>{s.n}</div>
                  <div style={{ marginBottom:12, color:colors.accent }}>
                    <EmojiIcon emoji={s.icon} size={28} />
                  </div>
                  <div style={{ fontSize:17, fontWeight:800, color:C.dark, marginBottom:10 }}>{s.title}</div>
                  <div style={{ fontSize:14, color:C.light, lineHeight:1.65 }}>{s.text}</div>
                </div>
              );
            })}
          </div>

          {/* Features */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(clamp(160px, 80vw, 240px), 1fr))", gap:"clamp(12px, 3vw, 20px)" }}>
            {t.features.map((f,i) => {
              const accent = [C.green,C.amber,C.orange,"#8B5CF6"][i];
              return (
                <div key={i} className="card-hover" style={{
                  background:C.white, borderRadius:18, padding:"28px 24px",
                  border:`1px solid ${C.border}`, boxShadow:"0 2px 12px rgba(0,0,0,0.05)",
                }}>
                  <div style={{ width:48, height:48, borderRadius:14, background:`${accent}15`, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:16, color:accent }}>
                    <EmojiIcon emoji={f.icon} size={22} />
                  </div>
                  <div style={{ fontSize:15, fontWeight:800, color:C.dark, marginBottom:8 }}>{f.title}</div>
                  <div style={{ fontSize:13, color:C.light, lineHeight:1.65 }}>{f.text}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ════ STATS ════ */}
      <section style={{ padding:"clamp(36px, 6vw, 70px) clamp(1rem, 7%, 7%)", background:`linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%)` }}>
        <div style={{ maxWidth:900, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <h2 style={{ fontSize:"clamp(1.5rem, 3vw, 2.2rem)", fontWeight:900, color:C.white, letterSpacing:"-0.03em", marginBottom:12 }}>
              {t.stats_h2}
            </h2>
          </div>
          <div style={{ display:"flex", gap:"clamp(12px, 3vw, 20px)", flexWrap:"wrap", justifyContent:"center" }}>
            {t.stats.map((s,i) => (
              <div key={i} style={{
                flex:"1 1 clamp(200px, 80%, 220px)", background:"rgba(255,255,255,0.12)",
                borderRadius:20, padding:"32px 24px", textAlign:"center",
                border:"1px solid rgba(255,255,255,0.2)",
              }}>
                <div style={{ marginBottom:10, color:C.white, display:"flex", justifyContent:"center" }}>
                  <EmojiIcon emoji={s.icon} size={28} />
                </div>
                <div style={{ fontSize:36, fontWeight:900, color:C.white, letterSpacing:"-0.04em", marginBottom:10 }}>{s.stat}</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,0.75)", lineHeight:1.65 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════ A — PLANES ════ */}
      <section ref={plansRef} style={{ padding:"clamp(40px, 8vw, 90px) clamp(1rem, 7%, 7%)", background:C.white }}>
        <div style={{ maxWidth:820, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:32 }}>
            <h2 style={{ fontSize:"clamp(1.6rem, 3.5vw, 2.4rem)", fontWeight:900, letterSpacing:"-0.03em", color:C.dark, marginBottom:12 }}>
              {t.plans_h2}
            </h2>
            <p style={{ fontSize:16, color:C.light }}>{t.plans_sub}</p>
          </div>

          {/* Toggle Mensual / Anual */}
          <div style={{ display:"flex", justifyContent:"center", marginBottom:36 }}>
            <div style={{
              display:"inline-flex", alignItems:"center", gap:4,
              background:"#F1F5F9", borderRadius:999, padding:4,
              border:`1px solid ${C.border}`,
            }}>
              {["mensual", "anual"].map(key => {
                const isActive = billing === key;
                return (
                  <button
                    key={key}
                    onClick={() => setBilling(key)}
                    style={{
                      display:"inline-flex", alignItems:"center", gap:8,
                      padding:"9px 22px", borderRadius:999,
                      background: isActive ? C.white : "transparent",
                      color: isActive ? C.dark : C.light,
                      border: "none", cursor:"pointer", fontFamily:"inherit",
                      fontSize:14, fontWeight:700,
                      boxShadow: isActive ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                      transition: "background 0.15s, color 0.15s",
                    }}
                  >
                    {billingLabels[key]}
                    {key === "anual" && (
                      <span style={{
                        fontSize:10, fontWeight:800, padding:"2px 8px", borderRadius:999,
                        background: isActive ? C.green : "rgba(5,150,105,0.15)",
                        color: isActive ? C.white : C.green,
                        textTransform:"uppercase", letterSpacing:"0.04em",
                      }}>
                        {billingLabels.save}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display:"flex", gap:"clamp(16px, 4vw, 24px)", flexWrap:"wrap" }}>
            {/* Free */}
            <div style={{ flex:"1 1 clamp(260px, 100%, 300px)", background:C.white, border:`2px solid ${C.border}`, borderRadius:24, padding:"clamp(24px, 4vw, 36px) clamp(20px, 4vw, 32px)", display:"flex", flexDirection:"column", boxShadow:"0 4px 20px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.light, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:12 }}>{t.free_label}</div>
              <div style={{ fontSize:44, fontWeight:900, color:C.dark, letterSpacing:"-0.04em", marginBottom:4 }}>{t.free_price}</div>
              <div style={{ fontSize:13, color:C.light, marginBottom:28 }}>{t.free_sub}</div>
              <div style={{ flex:1, marginBottom:28 }}>
                {t.free_features.map((f,i) => <Check key={i}>{f}</Check>)}
              </div>
              <button onClick={() => scrollTo(loginRef)} style={{ background:C.white, color:C.dark, border:`2px solid ${C.border}`, borderRadius:12, padding:"14px", fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"inherit", width:"100%" }}>
                {t.free_cta}
              </button>
            </div>
            {/* Pro */}
            <div style={{ flex:"1 1 clamp(260px, 100%, 300px)", background:`linear-gradient(160deg, #F0FDF4, #DCFCE7)`, border:`2px solid ${C.green}`, borderRadius:24, padding:"clamp(24px, 4vw, 36px) clamp(20px, 4vw, 32px)", display:"flex", flexDirection:"column", position:"relative", boxShadow:`0 8px 32px rgba(5,150,105,0.15)` }}>
              <div style={{ position:"absolute", top:24, right:24, background:C.amber, color:C.white, fontSize:10, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.06em", borderRadius:20, padding:"4px 12px" }}>
                {t.pro_badge}
              </div>
              <div style={{ fontSize:12, fontWeight:700, color:C.green, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:12 }}>{t.pro_label}</div>
              <div style={{ display:"flex", alignItems:"baseline", gap:6, marginBottom:4 }}>
                <div style={{ fontSize:44, fontWeight:900, color:C.dark, letterSpacing:"-0.04em" }}>${proPriceUSD.toFixed(2)}</div>
                <div style={{ fontSize:13, color:C.light, fontWeight:600 }}>{billingLabels.perMonth}</div>
              </div>
              {billing === "anual" && (
                <div style={{ fontSize:12, color:C.green, fontWeight:700, marginBottom:4 }}>
                  {billingLabels.billedAnnually}
                </div>
              )}
              {localPriceLabel && (
                <div
                  title="Equivalente aproximado según TRM del día. El cobro se procesa en USD vía Hotmart."
                  style={{ fontSize:12, color:C.medium, fontWeight:600, marginBottom:4, cursor:"help" }}
                >
                  ≈ {localPriceLabel} {userCurrency} / mes
                </div>
              )}
              <div style={{ fontSize:13, color:C.light, marginBottom:28 }}>{t.pro_sub}</div>
              <div style={{ flex:1, marginBottom:28 }}>
                {t.pro_features.map((f,i) => <Check key={i}>{f}</Check>)}
              </div>
              <button onClick={() => {
                  track("click_pago", { source: "landing_planes", plan: billing });
                  if (proCheckoutUrl) window.open(proCheckoutUrl,"_blank","noopener,noreferrer");
                  else scrollTo(loginRef);
                }}
                style={{ background:C.green, color:C.white, border:"none", borderRadius:12, padding:"14px", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"inherit", width:"100%", boxShadow:`0 6px 20px rgba(5,150,105,0.3)` }}>
                {t.pro_cta}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ════ FAQ ════ */}
      <FAQ lang={lang} colors={C} />

      {/* ════ A — LOGIN CTA ════ */}
      <section ref={loginRef} style={{ padding:"clamp(40px, 8vw, 90px) clamp(1rem, 5%, 7%)", background:`linear-gradient(160deg, ${C.white} 0%, ${C.greenBg} 100%)`, display:"flex", justifyContent:"center" }}>
        <div style={{ maxWidth:460, width:"100%", textAlign:"center" }}>
          <div style={{ marginBottom:16, color:C.green, display:"flex", justifyContent:"center" }}>
            <EmojiIcon emoji={t.login_icon} size={36} />
          </div>
          <h2 style={{ fontSize:"clamp(1.6rem, 3vw, 2.2rem)", fontWeight:900, color:C.dark, letterSpacing:"-0.03em", marginBottom:12 }}>
            {t.login_h2}
          </h2>
          <p style={{ fontSize:15, color:C.light, marginBottom:36, lineHeight:1.7 }}>{t.login_sub}</p>
          <div style={{ background:C.white, borderRadius:"clamp(16px, 3vw, 24px)", padding:"clamp(24px, 5vw, 40px) clamp(20px, 5vw, 44px)", boxShadow:"0 20px 60px rgba(0,0,0,0.10)", border:`1px solid ${C.border}` }}>
            <div style={{ marginBottom:24 }}><ZenviqLogo size={36} dark /></div>
            {loginLoading ? (
              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, height:48, color:C.light }}>
                <div style={{ width:20, height:20, border:`2px solid ${C.border}`, borderTop:`2px solid ${C.green}`, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
                <span style={{ fontSize:14 }}>{t.login_verifying}</span>
              </div>
            ) : (
              <div style={{ display:"flex", justifyContent:"center" }}>
                <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setLoginError("Error con Google.")} text="continue_with" shape="rectangular" size="large" width="320"/>
              </div>
            )}
            {loginError && (
              <div style={{ marginTop:14, padding:"10px 14px", background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:8, fontSize:13, color:"#DC2626" }}>{loginError}</div>
            )}
            <p style={{ fontSize:12, color:C.medium, marginTop:20, fontWeight:500 }}>{t.login_terms}</p>
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer style={{ borderTop:`1px solid ${C.border}`, background:C.white }}>
        {/* Fila superior — logo + derechos */}
        <div style={{ padding:"clamp(20px, 3vw, 32px) clamp(1rem, 7%, 7%)", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
          <ZenviqLogo size={24} dark />
          <span style={{ fontSize:12, color:C.light }}>
            © {new Date().getFullYear()} Zenviq · {t.footer_rights} · 318-798
          </span>
        </div>
        {/* Fila inferior — legal */}
        <div style={{ borderTop:`1px solid ${C.border}`, padding:"14px clamp(1rem, 7%, 7%)", display:"flex", alignItems:"center", gap:24, flexWrap:"wrap" }}>
          <button
            onClick={() => setModalLegal("terminos")}
            style={{ background:"none", border:"none", fontSize:12, color:C.light, cursor:"pointer", padding:0, fontFamily:"inherit", textDecoration:"underline" }}
          >
            Términos de Uso
          </button>
          <button
            onClick={() => setModalLegal("privacidad")}
            style={{ background:"none", border:"none", fontSize:12, color:C.light, cursor:"pointer", padding:0, fontFamily:"inherit", textDecoration:"underline" }}
          >
            Política de Privacidad
          </button>
          <span style={{ fontSize:12, color:C.border }}>·</span>
          <span style={{ fontSize:12, color:C.light }}>hola@zenviq.app</span>
        </div>
      </footer>

      {/* Modal legal */}
      {modalLegal && <ModalLegal tipo={modalLegal} onClose={() => setModalLegal(null)} />}
    </div>
  );
}
